import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogBody,
  Button,
  Textarea,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Dropdown,
  Option,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';
import { DocumentArrowUpRegular } from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestCorrectionDocuments } from '../services/api';
import type {
  CorrectionDocumentType,
  RequestCorrectionResponse,
} from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturnId: string;
  appointmentId?: string;
  clientName?: string;
  onSuccess?: (response: RequestCorrectionResponse) => void;
}

const DOCUMENT_TYPES: Array<{ value: CorrectionDocumentType; label: string; hint?: string }> = [
  {
    value: '1095-A',
    label: 'Form 1095-A (Marketplace Health Insurance)',
    hint: 'Email + SMS include how to obtain it: agent, Marketplace phone, HealthCare.gov.',
  },
  { value: 'W-2', label: 'Form W-2 (Wages)' },
  { value: '1099', label: 'Form 1099' },
  { value: 'other', label: 'Other document' },
];

export default function RequestCorrectionDialog({
  open,
  onOpenChange,
  taxReturnId,
  appointmentId,
  clientName,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient();

  const [documentType, setDocumentType] = useState<CorrectionDocumentType>('1095-A');
  const [notes, setNotes] = useState('');
  const [language, setLanguage] = useState<'en' | 'es'>('en');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      requestCorrectionDocuments(
        taxReturnId,
        {
          documentType,
          notes: notes.trim() || undefined,
          language,
        },
        appointmentId,
      ),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tax-return', taxReturnId] });
      queryClient.invalidateQueries({ queryKey: ['tax-returns'] });
      reset();
      onOpenChange(false);
      onSuccess?.(response);
    },
    onError: (err: Error) => setError(err.message),
  });

  function reset() {
    setDocumentType('1095-A');
    setNotes('');
    setLanguage('en');
    setError(null);
  }

  function handleSubmit() {
    setError(null);
    mutation.mutate();
  }

  const selected = DOCUMENT_TYPES.find((t) => t.value === documentType);
  const isSubmitting = mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!isSubmitting) onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Request correction documents</DialogTitle>
          <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--tl-text-secondary)' }}>
              Sends {clientName ? <strong>{clientName}</strong> : 'the client'} an email (with
              detailed instructions) and an SMS (short summary) asking for the document needed to
              finish the return. A secure upload link is generated and included in both messages.
            </div>

            <Field label="Document needed" required>
              <Dropdown
                value={selected?.label ?? ''}
                selectedOptions={[documentType]}
                onOptionSelect={(_, data) =>
                  setDocumentType((data.optionValue ?? '1095-A') as CorrectionDocumentType)
                }
                disabled={isSubmitting}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <Option key={t.value} value={t.value} text={t.label}>
                    {t.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            {selected?.hint && (
              <div style={{ fontSize: 12, color: 'var(--tl-text-secondary)', marginTop: -4 }}>{selected.hint}</div>
            )}

            <Field
              label="Note from preparer (optional)"
              hint="Appears in the email and upload portal. Kept short in the SMS."
            >
              <Textarea
                value={notes}
                onChange={(_, d) => setNotes(d.value)}
                rows={3}
                placeholder="e.g. Needed specifically for Marianne — her 1095-A only. Spouse's is already on file."
                disabled={isSubmitting}
              />
            </Field>

            <Field label="Language">
              <RadioGroup
                layout="horizontal"
                value={language}
                onChange={(_, d) => setLanguage(d.value as 'en' | 'es')}
                disabled={isSubmitting}
              >
                <Radio value="en" label="English" />
                <Radio value="es" label="Español" />
              </RadioGroup>
            </Field>

            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={isSubmitting ? <Spinner size="tiny" /> : <DocumentArrowUpRegular />}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending…' : 'Send request'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
